import { useTranslation } from 'react-i18next'
import type { TimeTrack } from '@/utils/planningTimeTracks'
import { INFO_COL_WIDTH } from '@/components/planning/ganttLayout'
import { BarraMarcas, useMarcasFilas } from '@/components/common/MarcasFilas'

/**
 * Cabecera fija del Gantt: celda de "PRODUCCIÓN" con el rango activo, más una
 * columna por tramo del eje.
 *
 * Los anchos salen del peso en días de cada columna (`weight` como unidades `fr`),
 * así que sirve tanto para los 7 días iguales de la vista semanal como para las
 * semanas recortadas de la mensual o los 12 meses de distinta longitud de la anual.
 */
export function GanttTrackHeader({ track, dark, titulo, anchoInfo, conMarcas = true }: {
  /** `TimeTrack<string>` y no `TimeTrack`: el eje del cuadro de muelles trae su
   *  propia vista ('dia' | 'semana'), que no es una `PlanVista`. */
  track: TimeTrack<string>
  dark: boolean
  /** Rótulo de la celda fija. Por defecto, el del Gantt de producción. */
  titulo?: string
  anchoInfo?: number
  /** La franja de recuento de marcas es del Gantt de producción; el cuadro de
   *  muelles no marca filas. */
  conMarcas?: boolean
}) {
  const { t } = useTranslation()
  // La franja de recuento vive aqui porque esta cabecera es lo unico del Gantt
  // que se pinta UNA sola vez: las filas van dentro de un grupo por ruta, y una
  // franja por grupo repetiria el mismo total tantas veces como rutas haya.
  const marcas = useMarcasFilas('gantt')
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const bdr      = dark ? 'border-white/10' : 'border-slate-200'

  return (
    <>
    {conMarcas && <BarraMarcas dark={dark} marcas={marcas} />}
    <div className={`flex border-b shrink-0 ${dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
      <div className={`shrink-0 px-3 py-2 border-r ${bdr}`} style={{ width: anchoInfo ?? INFO_COL_WIDTH }}>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>
          {titulo ?? t('planning.header.production')}
        </span>
        <div className={`text-[9px] mt-0.5 ${mutedTxt}`}>{track.label}</div>
      </div>
      <div
        className="flex-1"
        style={{ display: 'grid', gridTemplateColumns: track.columns.map(c => `${c.weight}fr`).join(' ') }}
      >
        {track.columns.map((col, i) => (
          <div
            key={i}
            className={`py-2 text-center border-r last:border-0 ${bdr}
              ${col.highlight ? (dark ? 'bg-sky-950/30' : 'bg-sky-50/60') : ''}`}
          >
            <div className={`text-[10px] font-bold ${col.highlight ? 'text-sky-500' : mutedTxt}`}>
              {col.primary}
            </div>
            {col.secondary && (
              <div className={`tabular-nums ${
                track.secondaryStrong ? 'text-[11px] font-black' : 'text-[10px] font-semibold'
              } ${col.highlight ? 'text-sky-500' : (dark ? 'text-slate-200' : 'text-slate-700')}`}>
                {col.secondary}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    </>
  )
}
